import Swal from 'sweetalert2';
import axios from 'axios';
import { formatPrice, validateNumber } from '@SHARED/js/helpers/misc';
import FormHelperMixin from '@SHARED/js/Vue/mixins/FormHelperMixin';
import template from './AddToCartComponent.html';
import store from '../../store/store';
import InputNumber from '../InputNumber/InputNumberComponent';
import ProductCartItem from '../../store/ProductCartItem';

export default {
  template,

  mixins: [FormHelperMixin],

  components: {
    InputNumber,
  },

  props: {
    // Note: this refers to our ID, not Visma ID
    productId: {
      type: Number,
      required: true,
    },
    productVismaId: {
      type: String,
      required: true,
    },
    productName: {
      type: String,
      required: true,
    },
    productPrice: {
      type: Number,
    },
    productCustomerPrices: {
      type: Array,
    },
    productWeight: {
      type: Number,
      default: 0,
    },
    productThumbnail: String,
    productCategory: String,
    productVariants: {
      type: Array,
      default: [],
    },
    productsToShowInDropdown: {
      type: Array,
      default: [],
    },
    showCurrentProductInDropdown: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      selectedVariant: null,
      inputData: {
        attachment_type: null,
        upload_file: null,
        arrow: null,
        fields: [],
      },
      quantity: 1,
      sharedState: store, // Do not modify this directly – this is only for convenience in templates.
      isUpdating: false,
      isUploading: false,
      fileInTransit: null,
    };
  },

  created() {
    this.setDefaultData();
  },

  watch: {
    selectedVariant(variant, oldVariant) {
      this.$parent.$emit('pricing-updated', {
        discountedPrice: this.getProductCartItemInstance().getQuantityDiscountPrice() + variant.additional_price,
        undiscountedPrice: this.getProductCartItemInstance().price + variant.additional_price,
      });

      if (!oldVariant) {
        // Ignore as this is most likely first run
        return;
      }

      const oldInputData = Object.assign({}, this.inputData);
      this.clearErrors();
      this.setDefaultData();

      if (variant.fields) {
        // Merge old user input with new variant input fields where applicable
        variant.fields.forEach((field, key) => {
          const inputValue = key in oldInputData.fields ? oldInputData.fields[key] : null;

          if (!_.isEmpty(inputValue)) {
            this.inputData.fields[key] = inputValue;
          }
        });
      }
    },
    'inputData.fields': function (val) {
      this.removeAttrError('message');

      val.forEach((field, key) => {
        this.validateField(field, key);
      });
    },
    'inputData.attachment_type': function () {
      this.removeAttrError('attachment_type');
    },
    errors(val) {
      if (!_.isEmpty(val)) {
        this.addAttrError('message', 'Bekreft at feltene er riktige');
      } else {
        this.removeAttrError('message');
      }
    },
    quantity() {
      // Hack in order to notify ProductShowWrapperModule
      this.$parent.$emit('pricing-updated', {
        discountedPrice:
          this.getProductCartItemInstance().getQuantityDiscountPrice()
          + (this.selectedVariant ? this.selectedVariant.additional_price : 0),
        undiscountedPrice:
          this.getProductCartItemInstance().price
          + (this.selectedVariant ? this.selectedVariant.additional_price : 0),
      });
    },
  },

  methods: {
    addToCart() {
      if (this.isUpdating) {
        // Prevent 'double submit'
        return;
      }

      let failedValidations = [];

      // Check required fields
      if (this.selectedVariant && !_.isEmpty(this.selectedVariant.fields)) {
        failedValidations = this.selectedVariant
          .fields
          .filter((field, key) => {
            if (field.required && _.isEmpty(this.inputData.fields[key])) {
              this.addAttrError(
                `field-${key}`,
                'Feltet er påkrevd',
              );

              return true;
            }

            return false;
          });
      }

      // Check if attachment type is required
      if (this.selectedVariant &&
        !_.isEmpty(this.selectedVariant.attachment_types) &&
        _.isEmpty(this.inputData.attachment_type)
      ) {
        this.addAttrError(
          'attachment_type',
          'Feltet er påkrevd',
        );

        // Cancel
        return;
      }

      if (failedValidations.length) {
        // Cancel
        return;
      }

      this.isUpdating = true;
      this.quantity = validateNumber(this.quantity);

      let variant = null;

      if (this.selectedVariant) {
        let fields = null;

        if (!_.isEmpty(this.selectedVariant.fields)) {
          fields = this.selectedVariant.fields.map((field, key) => {
            const inputValue = key in this.inputData.fields ? this.inputData.fields[key] : null;

            return { label: field.label, value: inputValue };
          });
        }

        variant = {
          id: this.selectedVariant.id,
          name: this.selectedVariant.name,
          additional_price: this.selectedVariant.additional_price,
          attachment_type: !_.isEmpty(this.inputData.attachment_type) ? this.inputData.attachment_type : null,
          upload_file: !_.isEmpty(this.inputData.upload_file) ? this.inputData.upload_file : null,
          arrow: !_.isEmpty(this.inputData.arrow) ? this.inputData.arrow : null,
          fields,
        };
      }

      const result = store.addToCart({
        id: this.productId,
        name: this.productName,
        vismaId: this.productVismaId,
        quantity: this.quantity,
        price: this.productPrice,
        customerPrices: this.productCustomerPrices,
        weight: this.productWeight,
        thumbnail: this.productThumbnail,
        category: this.productCategory,
        variant,
      });

      if (!result) {
        this.isUpdating = false;

        return;
      }

      setTimeout(() => {
        // Reset data
        this.setDefaultData();
      }, 2000);
    },

    getProductCartItemInstance() {
      return new ProductCartItem({
        id: this.productId,
        name: this.productName,
        vismaId: this.productVismaId,
        quantity: this.quantity,
        price: this.productPrice,
        customerPrices: this.productCustomerPrices,
        weight: this.productWeight,
        thumbnail: this.productThumbnail,
        variant: this.selectedVariant,
      });
    },

    increment() {
      this.quantity = validateNumber(this.quantity + 1);
    },

    decrement() {
      this.quantity = validateNumber(this.quantity - 1);
    },

    validateField(val, attr) {
      if (this.selectedVariant.fields[attr].type === 'numeric' && isNaN(val)) {
        this.addAttrError(
          `field-${attr}`,
          'Tall må være numerisk',
        );
      } else if (this.selectedVariant.fields[attr].num_chars > 0 &&
                 `${val}`.length > this.selectedVariant.fields[attr].num_chars
      ) {
        this.addAttrError(
          `field-${attr}`,
          `Kan ikke overstige ${this.selectedVariant.fields[attr].num_chars} tegn`,
        );
      } else if (this.selectedVariant.fields[attr].type !== 'numeric' &&
                 this.selectedVariant.fields[attr].num_lines &&
                 this.numLinesInText(val) > this.selectedVariant.fields[attr].num_lines
      ) {
        this.addAttrError(
          `field-${attr}`,
          `Kan ikke overstige ${this.selectedVariant.fields[attr].num_lines} linjer`,
        );
      } else {
        this.removeAttrError(`field-${attr}`);
      }
    },

    uploadFile(files) {
      this.removeAttrError('file');

      if (files[0] && files[0].size > 0) {
        this.fileInTransit = files[0];
      } else {
        return;
      }

      if (this.fileInTransit.size > (20 * 1000 * 1000)) {
        this.addAttrError('file', 'Filstørrelse for stor (maks 20mb)');

        return;
      }

      this.isUpdating = true;
      this.isUploading = true;

      const formData = new FormData();
      formData.append('file', this.fileInTransit);

      axios.post('/api/v1/upload-to-storage', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((response) => {
          this.inputData.upload_file = response.data.file;
        })
        .catch((error) => {
          if (typeof error.response.data.errors !== 'undefined') {
            this.errors = error.response.data.errors;
            this.addAttrError('message', 'Kunne ikke lagre fil.');
          } else {
            Swal.fire(
              'Noe gikk galt',
              'Vennligst prøv igjen. Om problemet vedvarer, ring Kraftex (<a href="tel:69252530">69 25 25 30</a>).<br>' +
              'Feil utenom våre åpningstider melder du <a href="/kontakt-oss" target="_blank">her</a>, så tar vi kontakt med deg.',
              'error',
            );

            window.bugsnagClient && window.bugsnagClient.notify(error);
            throw new Error(error);
          }
        })
        .finally(() => {
          this.isUpdating = false;
          this.isUploading = false;
          this.$refs.fileInput.value = null;
        });
    },

    removeUploadFile() {
      this.removeAttrError('file');
      this.fileInTransit = null;
      this.inputData.upload_file = '';
      this.$refs.fileInput.value = null;
    },

    setDefaultData() {
      if (!this.selectedVariant) {
        this.selectedVariant = this.productVariants.length ? this.productVariants[0] : null;
      }

      if (this.selectedVariant && !_.isEmpty(this.selectedVariant.attachment_types)) {
        if (this.selectedVariant.attachment_types.length === 1) {
          this.inputData.attachment_type = this.selectedVariant.attachment_types[0];
        } else {
          this.inputData.attachment_type = null;
        }
      }

      this.inputData.fields = [];
      this.inputData.arrow = null;
      this.fileInTransit = null;
      this.inputData.upload_file = null;
      this.quantity = 1;

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }

      this.isUpdating = false;
    },

    redirectToProduct(event) {
      window.location.href = event.target.value;
    },

    // Helpers

    typeToHtmlInputType(type) {
      if (type === 'numeric') {
        return 'number';
      }

      if (type === 'alpha_num') {
        return 'text';
      }

      // Type is null or "string":
      return 'text';
    },

    fieldTypeToHumanText(type) {
      if (type === 'numeric') {
        return 'tall';
      }

      if (type === 'alpha_num') {
        return 'alfanumerisk [A-å 0-9]';
      }

      // Type is null or "string":
      return 'tekst';
    },

    attachmentTypeToHumanText(type) {
      if (type === 'nothing') {
        return 'intet feste';
      }

      if (type === 'hole') {
        return 'hull';
      }

      if (type === 'tape') {
        return 'tape';
      }

      return type;
    },

    numLinesInText(text) {
      return (text.match(/\n/g) || []).length + 1;
    },

    formatPrice,
  },
};
